import { useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IconSun, IconMoon } from './Icons';

/**
 * Mobile Sidebar Component
 * Implements sliding entrance, staggering items, and backdrop blur.
 */
export default function Sidebar({ isOpen, onClose, navLinks, theme, onToggleTheme }) {
  
  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent scroll
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = ''; // Restore scroll
    };
  }, [isOpen, onClose]);

  const sidebarVariants = {
    closed: { 
      x: '100%',
      transition: { duration: 0.4, ease: [0.32, 0, 0.67, 0] }
    },
    open: { 
      x: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const overlayVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 }
  };

  const containerVariants = {
    open: {
      transition: { staggerChildren: 0.08, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 20 },
    open: { opacity: 1, x: 0, transition: { duration: 0.4, ease: 'easeOut' } }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Blur Overlay */}
          <motion.div
            className="navbar__overlay"
            initial="closed"
            animate="open"
            exit="closed"
            variants={overlayVariants}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Sidebar Panel (85% width) */}
          <motion.aside
            className="navbar__drawer"
            initial="closed"
            animate="open"
            exit="closed"
            variants={sidebarVariants}
            role="dialog"
            aria-label="Mobile navigation"
          >
            <div className="navbar__drawer-header">
              <Link to="/" className="navbar__brand" onClick={onClose}>
                <img src="/dr sofjan/2.jpeg" alt="Dicky Sofjan logo" className="navbar__brand-monogram" />
                <span className="navbar__brand-text">
                  <strong>Dicky Sofjan</strong>
                </span>
              </Link>
              <button 
                className="navbar__hamburger navbar__hamburger--open" 
                onClick={onClose}
                aria-label="Close menu"
              >
                <span></span><span></span><span></span>
              </button>
            </div>

            <motion.nav 
              className="navbar__drawer-nav"
              variants={containerVariants}
            >
              {navLinks.map((link) => (
                <motion.div key={link.to} variants={itemVariants}>
                  <NavLink
                    to={link.to}
                    onClick={onClose}
                    className={({ isActive }) => `navbar__drawer-link ${isActive ? 'navbar__drawer-link--active' : ''}`}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </motion.nav>

          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
